import { useReducer, useCallback } from 'react';

// Estado inicial
const initialState = {
    selectedRows: [],
    modals: {
        confirm: false,
        result: false,
        descartar: false,
        reprocesar: false,
    },
    alert: {
        show: false,
        variant: 'success',
        title: '',
        description: '',
        containerStyle: '',
        descriptionStyle: '',
        titleStyle: '',
    },
    resultData: null,
    isProcessing: false,
};

// Tipos de acciones
const ACTIONS = {
    SET_SELECTED_ROWS: 'SET_SELECTED_ROWS',
    OPEN_MODAL: 'OPEN_MODAL',
    CLOSE_MODAL: 'CLOSE_MODAL',
    SHOW_ALERT: 'SHOW_ALERT',
    HIDE_ALERT: 'HIDE_ALERT',
    SET_RESULT_DATA: 'SET_RESULT_DATA',
    SET_PROCESSING: 'SET_PROCESSING',
    RESET_SELECTION: 'RESET_SELECTION',
};

// Reducer
function uiReducer(state, action) {
    switch (action.type) {
        case ACTIONS.SET_SELECTED_ROWS:
            return {
                ...state,
                selectedRows: action.payload,
            };

        case ACTIONS.OPEN_MODAL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    [action.payload]: true,
                },
            };

        case ACTIONS.CLOSE_MODAL:
            return {
                ...state,
                modals: {
                    ...state.modals,
                    [action.payload]: false,
                },
            };

        case ACTIONS.SHOW_ALERT:
            return {
                ...state,
                alert: {
                    show: true,
                    ...action.payload,
                },
            };

        case ACTIONS.HIDE_ALERT:
            return {
                ...state,
                alert: {
                    ...initialState.alert,
                },
            };

        case ACTIONS.SET_RESULT_DATA:
            return {
                ...state,
                resultData: action.payload,
            };

        case ACTIONS.SET_PROCESSING:
            return {
                ...state,
                isProcessing: action.payload,
            };

        case ACTIONS.RESET_SELECTION:
            if (state.selectedRows.length === 0) {
                return state;
            }
            return {
                ...state,
                selectedRows: [],
            };

        default:
            return state;
    }
}

/**
 * Hook personalizado para manejar el estado de UI del componente MatchingJudicial
 * Centraliza el manejo de modales, alertas, selección de filas y estado de procesamiento
 */
export function useMatchingJudicialUI() {
    const [state, dispatch] = useReducer(uiReducer, initialState);

    // Handlers
    const setSelectedRows = useCallback((rows) => {
        dispatch({ type: ACTIONS.SET_SELECTED_ROWS, payload: rows });
    }, []);

    const openModal = useCallback((modalName) => {
        dispatch({ type: ACTIONS.OPEN_MODAL, payload: modalName });
    }, []);

    const closeModal = useCallback((modalName) => {
        dispatch({ type: ACTIONS.CLOSE_MODAL, payload: modalName });
    }, []);

    const showAlert = useCallback((alertConfig) => {
        dispatch({ type: ACTIONS.SHOW_ALERT, payload: alertConfig });
    }, []);

    const hideAlert = useCallback(() => {
        dispatch({ type: ACTIONS.HIDE_ALERT });
    }, []);

    const setResultData = useCallback((data) => {
        dispatch({ type: ACTIONS.SET_RESULT_DATA, payload: data });
    }, []);

    const setProcessing = useCallback((isProcessing) => {
        dispatch({ type: ACTIONS.SET_PROCESSING, payload: isProcessing });
    }, []);

    const resetSelection = useCallback(() => {
        dispatch({ type: ACTIONS.RESET_SELECTION });
    }, []);

    return {
        // Estado
        selectedRows: state.selectedRows,
        modals: state.modals,
        alert: state.alert,
        resultData: state.resultData,
        isProcessing: state.isProcessing,

        // Handlers
        setSelectedRows,
        openModal,
        closeModal,
        showAlert,
        hideAlert,
        setResultData,
        setProcessing,
        resetSelection,
    };
}
